import debounce from 'lodash/debounce';

import { NEAR_ME_LABEL } from '@/constants/location';
import { csrfToken } from '@/utils';
import { zoomOutByKm } from '@/utils/geo';

import { setInitialLocation } from './location-page.autocomplete-utils';

export const init = () => {
  const $input = $('.js-google-location-autocomplete');
  const $error = $('.js-google-location-autocomplete-error-msg');
  let initialLocation = null;
  let nearMeActive = false;

  if (window.__TGV_SEARCH_FORM_DATA?.label) {
    $input.val(window.__TGV_SEARCH_FORM_DATA.label);
  }

  const geoLocationSupported = 'geolocation' in navigator;

  const showError = (text) => {
    $error.show().text(text);
  };

  const hideError = () => {
    $error.hide();
  };

  const getSelection = () => {
    if (initialLocation) {
      return initialLocation;
    }

    return $($input).autocomplete().selection;
  };

  const textEscape = (data) => {
    const div = document.createElement('div');
    div.innerText = data;
    return div.innerHTML;
  };

  const locationUnknownWarningEvent = debounce((text) => {
    App.track.event('location_page_interaction', {
      section: 'header',
      element: 'location_unknown_warning',
      element_value: text,
      interaction: 'visible',
    });
  }, 500);

  const params = {
    _token: csrfToken(),
  };

  // must be bound before the autocomplete plugin, so the sentinel is cleared before the plugin's focus handler queries it
  $input.on('focus', () => {
    if (nearMeActive && $input.val() === NEAR_ME_LABEL) {
      $input.val('');
    }
  });

  $input.on('blur', () => {
    if (nearMeActive && $input.val().length === 0) {
      $input.val(NEAR_ME_LABEL);
    }
  });

  $input.autocomplete({
    onSearchStart: () => {
      // default params
      params.defaultCityList = 0;
      params.country = window.__TGV_ISO_COUNTRY_CODE;

      // only for HP (country page)
      if (window.__TGV_SEARCH__COUNTRY_PAGE) {
        if ($input.val().length === 0 || window.__TGV_SEARCH_FORM_DATA.label === $input.val() || initialLocation?.value === $input.val()) {
          params.defaultCityList = 1;
        } else {
          params.country = null; // no limitations by country
        }
      }

      return true;
    },
    width: matchMedia('(max-width: 600px)').matches ? 'auto' : 340,
    maxHeight: 320,
    type: 'POST',
    groupBy: 'group',
    serviceUrl: window.__TGV_AUTOCOMPLETE_GOOGLE_ROUTES.AUTOCOMPLETE, // /api/location-autocomplete
    dataType: 'json',
    deferRequestBy: 100,
    paramName: 'location',
    params,
    beforeRender(container, suggestions) {
      hideError();

      container
        .addClass('autocomplete-suggestions--googleLocation')
        .append($('<div class="autocomplete-suggestions__google">Powered by Google</div>'));

      if (geoLocationSupported) {
        container.prepend($(`<div class="autocomplete-suggestion--findMe">${NEAR_ME_LABEL}</div>`));
      }

      container[!suggestions.length && !$input.val() ? 'addClass' : 'removeClass']('autocomplete-suggestions--noQuery');
    },
    transformResult(response) {
      const results = response.map((place) => {
        if (place.data?.locationId) {
          return {
            value: place.value,
            data: {
              locationId: place.data.locationId,
              subdir: place.data.subdir,
              group: place.data.group,
            },
          };
        }

        if (place.place_id) {
          return {
            value: place.description,
            data: {
              place_id: place.place_id,
              label: place.description,
            },
          };
        }

        throw new Error('Invalid response from autocomplete API');
      });

      return {
        suggestions: results,
      };
    },
    onSearchComplete(text, results) {
      if (text.length && !results.length) {
        locationUnknownWarningEvent(text);
      }

      if (text.length && initialLocation?.value !== text) {
        initialLocation = null;
      }

      if (text.length && text !== NEAR_ME_LABEL) {
        nearMeActive = false;
      }
    },
    autoSelectFirst: true,
    triggerSelectOnValidInput: false,
    showNoSuggestionNotice: true,
    minChars: geoLocationSupported ? 0 : 1,
    preventBadQueries: false, // need to do it because of min chars 0 and displaying Current location option when no input
    noSuggestionNotice: '<span>Unfortunately, Tagvenue is not yet available in this location</span>',
    formatResult(suggestion, query) {
      const text = textEscape(suggestion.value);

      // Escape special regex characters in the query
      const escapedQuery = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      return `<div>${text.replace(new RegExp(`(${escapedQuery})`, 'gi'), '<strong>$1</strong>')}</div>`;
    },
    onSelect(data) {
      initialLocation = null;
      nearMeActive = false;
      $error.hide();

      window.googleAutoCompleteForceRoomTag = false;
      window.googleAutoCompleteLocationBounds = null;
      window.googleAutoCompleteLocationFormattedAddress = null;

      if (data.data?.locationId) {
        // redirect to search with locationId
        window.__TGV_SEARCH_FORM_DATA.locationId = data.data.locationId;
        return;
      }

      window.googleAutoCompleteLocationBoundsIsLoading = true;

      // /api/place-details?place_id=...
      $.post(window.__TGV_AUTOCOMPLETE_GOOGLE_ROUTES.PLACE_DETAILS, {
        place_id: data.data.place_id,
        _token: csrfToken(),
      }).then((response) => {
        if (!response) {
          return;
        }

        // check real dimensions of area (in kilometers)
        const wide = response.northeast.lng - response.southwest.lng;
        const high = response.northeast.lat - response.southwest.lat;
        const wideKm = wide * 111.32 * Math.cos(response.northeast.lat * (Math.PI / 180));
        const highKm = high * 111.32;

        if (wideKm < 1 || highKm < 1) {
          // get center of the location and zoom out 1km
          const lat = (response.northeast.lat + response.southwest.lat) / 2;
          const lng = (response.northeast.lng + response.southwest.lng) / 2;
          window.googleAutoCompleteLocationBounds = {
            ...zoomOutByKm(lat, lng, 0.5),
            subdir: response.subdir,
          };
        } else {
          window.googleAutoCompleteLocationBounds = {
            latitude_from: response.southwest.lat,
            latitude_to: response.northeast.lat,
            longitude_from: response.southwest.lng,
            longitude_to: response.northeast.lng,
            subdir: response.subdir,
          };
        }

        window.googleAutoCompleteLocationBoundsIsLoading = false;

        window.googleAutoCompleteLocationFormattedAddress = data.value;
      });

      App.track.event('location_page_interaction', {
        section: 'header',
        element: 'location',
        element_value: data.value,
        interaction: 'change',
      });
    },
  });

  $input.data('getSelection', getSelection);

  if (window.__TGV_SEARCH__COUNTRY_PAGE) {
    setInitialLocation($input).then(data => {
      initialLocation = data;
      window.__TGV_SEARCH_FORM_DATA.locationId = data.data.locationId;
    });
  }

  if (geoLocationSupported) {
    $(document).on('click', '.autocomplete-suggestion--findMe', (e) => {
      e.preventDefault();
      e.stopPropagation();

      $input.val('');

      App.track.event('location_page_interaction', {
        section: 'header',
        element: 'location_field_current_location',
        interaction: 'click',
      });

      navigator.geolocation.getCurrentPosition((position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        App.track.event('location_page_interaction', {
          section: 'header',
          element: 'location_field_current_location',
          interaction: 'position_found',
        });

        window.googleAutoCompleteLocationBounds = zoomOutByKm(lat, lng, 2);
        window.googleAutoCompleteLocationFormattedAddress = NEAR_ME_LABEL;

        nearMeActive = true;
        $input.val(NEAR_ME_LABEL);
        $input.autocomplete().hide();

        hideError();
      }, (err) => {
        App.track.event('location_page_interaction', {
          section: 'header',
          element: 'location_field_current_location',
          element_value: err.message || null,
          interaction: 'position_error',
        });

        // close the autocomplete suggestions
        $input.autocomplete().hide();

        showError('Unable to get your location. Please enter it manually');

        setTimeout(() => {
          $error.hide();
        }, 5000);

        console.log(err);
      });
    });
  }

  const checkIfValid = () => {
    if (initialLocation?.value === $input.val() || window.__TGV_SEARCH_FORM_DATA.label === $input.val()) {
      return true;
    }
    return !!getSelection() || window.googleAutoCompleteLocationBounds;
  };

  return {
    validateIsEmpty() {
      if (!checkIfValid()) {
        showError('Please enter a location');
      } else {
        hideError();
      }
    },
    isValid() {
      return checkIfValid();
    },
    /**
     * Fills the field with a Tagvenue location, as if it had been picked from the suggestions.
     */
    setLocation({ label, locationId, subdir }) {
      initialLocation = { value: label, data: { locationId, subdir } };
      nearMeActive = false;
      window.googleAutoCompleteLocationBounds = null;
      window.googleAutoCompleteLocationFormattedAddress = null;
      $input.val(label);
      $input.autocomplete().hide();
      hideError();
    },
  };
};
